import guidesail from 'guidesail'

import locator from '@/service-locator'
import { parseQueryString } from '@/utils/query-string'

const PRODUCT_TOUR_KEY = 'tamara-product-tour'

const parseProductTourCached = (productTours) => {
  if (!productTours) {
    return {}
  }

  try {
    return JSON.parse(productTours) || {}
  } catch (_) {
    return {}
  }
}

class ProductTourStorage {
  constructor() {
    this.tours = {}

    this.syncToursFromCached()
  }

  syncToursFromCached = () => {
    const cachedTours = localStorage.getItem(PRODUCT_TOUR_KEY)

    this.tours = parseProductTourCached(cachedTours)
  }

  persistToursToCached = () => {
    localStorage.setItem(PRODUCT_TOUR_KEY, JSON.stringify(this.tours))
  }

  addShownTour = (tourId, userEmail) => {
    if (!this.tours[userEmail]) {
      this.tours[userEmail] = []
    }

    if (!this.isUserShownTour(tourId, userEmail)) {
      this.tours[userEmail].push(tourId)

      this.persistToursToCached()
    }
  }

  isUserShownTour = (tourId, userEmail) => {
    return this.tours?.[userEmail]?.includes(tourId)
  }
}

class ProductTourService {
  constructor(guidesailKey) {
    this.productTourStorage = new ProductTourStorage()

    if (!guidesailKey) {
      console.warn('[Tamara] - Product tour is missing key!!!')

      return
    }

    if (this._isDisabledProductTourOnNonProduction()) {
      console.warn(
        '[Tamara] - Product tour is disabled by URL on non-production env!!!'
      )

      return
    }

    guidesail.sail(guidesailKey)

    this.addEventListeners()
  }

  _isDisabledProductTourOnNonProduction = () => {
    const searchParams = parseQueryString(window.location.hash)
    const disabledProductTour =
      searchParams?.__test__disabled_product_tour ?? false

    return !locator.config.isProduction && disabledProductTour
  }

  _handleEventEnded = (e) => {
    const detail = e.detail || {}
    const productTourId = detail?.guideId || ''
    const userProfile = locator.store.getters['userProfile/profile']
    const userEmail = userProfile?.email || ''

    this.productTourStorage.addShownTour(productTourId, userEmail)
  }

  addEventListeners = () => {
    window.addEventListener('guideEnded', this._handleEventEnded)
  }

  removeEventListeners = () => {
    window.removeEventListener('guideEnded', this._handleEventEnded)
  }

  changeTourByRoute = (nextTo) => {
    // Disable product tour on non-production env
    if (this._isDisabledProductTourOnNonProduction()) {
      return
    }

    const productTourConfig = nextTo?.meta?.productTour
    const isArabic = locator.store.getters['translations/isArabic']
    const userProfile = locator.store.getters['userProfile/profile']
    const userEmail = userProfile?.email || ''

    let productTourId = productTourConfig?.idEn

    if (isArabic) {
      productTourId = productTourConfig?.idAr
    }

    if (!productTourId) {
      return
    }

    this.launch(productTourId, userEmail)
  }

  launch = (productTourId, userEmail = '') => {
    if (!this.productTourStorage.isUserShownTour(productTourId, userEmail)) {
      guidesail.launch(productTourId)
    }
  }

  destroy = () => {
    this.removeEventListeners()
  }
}

const buildProductTourService = (locator) => {
  locator.productTour = new ProductTourService(locator.config.guidesailKey)
}

export { buildProductTourService }
